import {
    Card,
    Typography,
    Stack,
    Box,
    FormControlLabel,
    Button,
    Select,
    MenuItem,
    IconButton,
    Switch
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import axios from "axios";

const ReviewSection = ({ questions, video, videoIds, currentQuestionIndex, onQuestionChange }) => {
    const [videos, setVideos] = useState(video || []);
    const [videoIdsState, setVideoIdsState] = useState(videoIds || []);
    const [autoplayEnabled, setAutoplayEnabled] = useState(false);
    const videoRef = useRef(null);

    // Initialize videos and videoIds when props change
    useEffect(() => {
        const newVideos = video || [];
        const newVideoIds = videoIds || [];
        setVideos(newVideos);
        setVideoIdsState(newVideoIds);
    }, [video, videoIds]);

    // Check if current video exists
    const currentVideoExists = videos && currentQuestionIndex >= 0 && videos[currentQuestionIndex]?.original_url;

    // Handle autoplay when the switch is toggled or question changes
    useEffect(() => {
        if (videoRef.current) {
            if (autoplayEnabled && currentVideoExists) {
                // Try to play the video when autoplay is enabled
                const playPromise = videoRef.current.play();

                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.log("Autoplay was prevented:", error);
                    });
                }
            } else {
                // Pause the video when autoplay is disabled
                videoRef.current.pause();
            }
        }
    }, [autoplayEnabled, currentVideoExists, currentQuestionIndex]);

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            onQuestionChange(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            onQuestionChange(currentQuestionIndex - 1);
        }
    };

    const handleJumpTo = (event) => {
        onQuestionChange(Number(event.target.value));
    };

    const handleDeleteVideo = () => {
        if (
            videoIdsState.length > 0 &&
            currentQuestionIndex >= 0 &&
            currentQuestionIndex < videoIdsState.length
        ) {
            const videoIdToDelete = videoIdsState[currentQuestionIndex];

            axios
                .get(`https://api.a2ajobs.com/api/delete-candidate-video/${videoIdToDelete}`)
                .then((response) => {
                    console.log("Video deleted successfully", { videoIdToDelete });
                    alert("Video deleted successfully!");

                    // Update local state to remove the video
                    setVideos((prevVideos) => prevVideos.filter((_, index) => index !== currentQuestionIndex));
                    setVideoIdsState((prevIds) => prevIds.filter((_, index) => index !== currentQuestionIndex));
                })
                .catch((error) => {
                    console.error("Error deleting video:", error);
                    alert("Error deleting video. Please try again.");
                });
        }
    };

    return (
        <Card sx={{ p: 2, width: "90%", mb: { xs: 2, md: 0 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
                <IconButton onClick={handlePrevious} disabled={currentQuestionIndex === 0}>
                    <ArrowBack color="primary" />
                    <Typography color="primary">Previous </Typography>
                </IconButton>

                <Select value={currentQuestionIndex} onChange={handleJumpTo} size="small">
                    {questions?.map((q, index) => (
                        <MenuItem key={q.id} value={index}>
                            {`Question ${index + 1}`}
                        </MenuItem>
                    ))}
                </Select>

                <IconButton onClick={handleNext} disabled={currentQuestionIndex === questions?.length - 1}>
                    <ArrowForward color="primary" />
                    <Typography color="primary">Next </Typography>
                </IconButton>
            </Stack>

            {/* Display current video or message if not available */}
            {currentVideoExists ? (
                <>
                    <video
                        width="100%"
                        controls
                        src={videos[currentQuestionIndex]?.original_url}
                        key={currentQuestionIndex}
                        ref={videoRef}
                        autoPlay={autoplayEnabled}
                    />
                </>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        height: "300px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        border: "1px dashed #ccc"
                    }}
                >
                    <Typography variant="h6" color="textSecondary">
                        No video found for this question
                    </Typography>
                </Box>
            )}

            <Stack direction="row" gap={3} mt={2} alignItems="center">
                <FormControlLabel
                    control={
                        <Switch
                            checked={autoplayEnabled}
                            onChange={(e) => setAutoplayEnabled(e.target.checked)}
                        />
                    }
                    label="Autoplay"
                />
                <Button
                    variant="text"
                    color="error"
                    onClick={handleDeleteVideo}
                    disabled={!currentVideoExists}
                >
                    Delete This Video
                </Button>
            </Stack>

            <Box sx={{ backgroundColor: "#f9f9f9", p: 2, mt: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                    {questions?.[currentQuestionIndex]?.question_title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {questions?.[currentQuestionIndex]?.full_question}
                </Typography>
            </Box>
        </Card>
    );
};

export default ReviewSection;